/**
 * Shared extraction rendering utilities.
 *
 * Extracted from AppComponent so both AppComponent and
 * ExtractionReviewComponent can reuse the same helpers
 * without duplication.
 */

// ── Constants ────────────────────────────────────────────────────────────────

/** Keys hidden from the tree view (internal metadata). */
export const HIDDEN_KEYS = new Set([
  'token_usage',
  'document_type',
  'extraction_language',
  'extraction_model',
  'billing_tokens',
]);

// ── Enums ────────────────────────────────────────────────────────────────────

export enum ViewMode {
  TREE = 'tree',
  JSON = 'json',
  SPREADSHEET = 'spreadsheet',
}

// ── Interfaces ───────────────────────────────────────────────────────────────

export interface XmlTreeNode {
  tag: string;
  text: string;
  attributes: Record<string, string>;
  children: XmlTreeNode[];
  _collapsed?: boolean;
}

// ── Pure helper functions ────────────────────────────────────────────────────

export function isArray(value: unknown): boolean {
  return Array.isArray(value);
}

export function isObject(value: unknown): boolean {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function getObjectKeys(obj: unknown): string[] {
  if (!obj || typeof obj !== 'object') return [];
  return Object.keys(obj);
}

export function getVisibleKeys(obj: unknown): string[] {
  return getObjectKeys(obj).filter(k => !HIDDEN_KEYS.has(k));
}

export function formatValue(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'string') return `"${value}"`;
  if (Array.isArray(value)) return `[${value.length} items]`;
  if (typeof value === 'object') return `{${Object.keys(value as object).length} fields}`;
  return String(value);
}

// ── Value + confidence pair detection ─────────────────────────────────────────

/**
 * Detect if an object is a {value} or {value, confidence} leaf pair.
 * Only collapses when there are no other keys — objects with additional
 * fields need indentation to stay readable.
 */
export function asValueConfidencePair(obj: unknown): { value: string; confidence: number | null } | null {
  if (!isObject(obj)) return null;
  const o = obj as Record<string, unknown>;
  const keys = Object.keys(o);
  if (keys.length === 1 && keys.includes('value')) {
    return { value: formatRawValue(o['value']), confidence: null };
  }
  if (keys.length === 2 && keys.includes('value') && keys.includes('confidence')) {
    const conf = typeof o['confidence'] === 'number' ? o['confidence'] : null;
    return { value: formatRawValue(o['value']), confidence: conf };
  }
  return null;
}

/** Format a raw value for inline display (no quotes around strings). */
function formatRawValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

/** Return CSS class for confidence level. */
export function confidenceClass(confidence: number | null): string {
  if (confidence === null) return '';
  const pct = confidence <= 1 ? confidence * 100 : confidence;
  if (pct > 95) return 'confidence-green';
  if (pct > 90) return 'confidence-light-green';
  if (pct > 85) return 'confidence-yellow';
  if (pct >= 80) return 'confidence-orange';
  return 'confidence-red';
}

/** Format confidence as percentage string. */
export function confidencePercent(confidence: number | null): string {
  if (confidence === null) return '';
  const pct = confidence <= 1 ? confidence * 100 : confidence;
  return Math.round(pct) + '%';
}

// ── Result type detection ────────────────────────────────────────────────────

export function isXmlExtractionResult(result: unknown): boolean {
  return isObject(result) && 'xml' in (result as object);
}

export function isTableExtractionResult(result: unknown): boolean {
  if (!isObject(result)) return false;
  const r = result as Record<string, unknown>;
  return !!r['format'] && ['csv', 'odf', 'xlsx'].includes(r['format'] as string);
}

// ── XML tree parsing ─────────────────────────────────────────────────────────

function elementToTreeNode(el: Element): XmlTreeNode {
  const node: XmlTreeNode = {
    tag: el.localName || el.tagName,
    text: '',
    attributes: {},
    children: [],
  };

  for (let i = 0; i < el.attributes.length; i++) {
    const attr = el.attributes[i];
    if (attr.name !== 'xmlns') {
      node.attributes[attr.name] = attr.value;
    }
  }

  const childElements: Element[] = [];
  for (let i = 0; i < el.childNodes.length; i++) {
    const child = el.childNodes[i];
    if (child.nodeType === Node.ELEMENT_NODE) {
      childElements.push(child as Element);
    }
  }

  if (childElements.length > 0) {
    node.children = childElements.map(c => elementToTreeNode(c));
  } else {
    node.text = (el.textContent || '').trim();
  }

  return node;
}

export function parseXmlToTree(xml: string): XmlTreeNode | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'application/xml');
    const errorNode = doc.querySelector('parsererror');
    if (errorNode) return null;
    return elementToTreeNode(doc.documentElement);
  } catch {
    return null;
  }
}

// ── CSV helpers ──────────────────────────────────────────────────────────────

/** Parsed CSV table structure. */
export interface CsvTable {
  headers: string[];
  rows: string[][];
}

/** Returns true when the extraction result contains a CSV string. */
export function isCsvExtractionResult(result: unknown): boolean {
  return isObject(result) && typeof (result as Record<string, unknown>)['csv'] === 'string';
}

/**
 * Parse an RFC 4180 CSV string into a 2D array of strings.
 * Handles quoted fields with embedded commas, newlines, and escaped quotes.
 */
export function parseCsv(str: string): string[][] {
  const rows: string[][] = [];
  const s = str.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  let i = 0;
  while (i < s.length) {
    // Skip blank lines between records
    if (s[i] === '\n') { i++; continue; }
    const fields: string[] = [];
    while (i <= s.length) {
      if (i < s.length && s[i] === '"') {
        // Quoted field — may span multiple lines
        let field = '';
        i++;
        while (i < s.length) {
          if (s[i] === '"' && i + 1 < s.length && s[i + 1] === '"') {
            field += '"'; i += 2;
          } else if (s[i] === '"') {
            i++; break;
          } else {
            field += s[i++];
          }
        }
        fields.push(field);
        if (i < s.length && s[i] === ',') { i++; continue; }
      } else {
        // Unquoted field — ends at comma or newline
        let field = '';
        while (i < s.length && s[i] !== ',' && s[i] !== '\n') {
          field += s[i++];
        }
        fields.push(field);
        if (i < s.length && s[i] === ',') { i++; continue; }
      }
      // End of record (newline or EOF)
      if (i < s.length && s[i] === '\n') i++;
      break;
    }
    if (fields.length > 0 && !(fields.length === 1 && fields[0] === '')) {
      rows.push(fields);
    }
  }
  return rows;
}

/** Parse a CSV string into a CsvTable (header + data rows). Returns null if fewer than 2 rows. */
export function parseCsvTable(csvString: string): CsvTable | null {
  const parsed = parseCsv(csvString);
  if (parsed.length < 2) return null;
  return { headers: parsed[0], rows: parsed.slice(1) };
}
